import { useEffect, useRef, useState } from 'react'
import { hasSettingsChanged } from '../helper'
import { ACCOUNT_SETTINGS_RELOAD_PAGE_POP_UP_TEXT } from '../../../utils/constants'
import type { ApplicationSettingsPerStateAdminApiContract } from '@/api/generated/pwrdbyQuickMDAdminsApi.schemas'

interface UseEditStateSettingsEffectsParams {
  error: string
  stateSettings: ApplicationSettingsPerStateAdminApiContract
  requiresDrugScreening: boolean | null | undefined
  setError: (value: string) => void
  setIsConfirmationPopUpOpened: (value: boolean | 'back') => void
  isPhoneVisitAllowed: boolean | undefined
  drugScreeningText: string | null | undefined
  setIsPhoneVisitAllowed: (value: boolean) => void
  setDrugScreeningText: (value: string | null) => void
  setRequiresDrugScreening: (value: boolean) => void
  drugError: string
  setDrugError: (value: string) => void
}

export const useEditStateSettingsEffects = ({
  error,
  stateSettings,
  requiresDrugScreening,
  setError,
  setIsConfirmationPopUpOpened,
  isPhoneVisitAllowed,
  drugScreeningText,
  setIsPhoneVisitAllowed,
  setDrugScreeningText,
  setRequiresDrugScreening,
  drugError,
  setDrugError,
}: UseEditStateSettingsEffectsParams) => {
  const hasUpdated = useRef<{
    changed?: boolean
    isPhoneVisitAllowed?: boolean | undefined
    drugScreeningText?: string | null | undefined
    requiresDrugScreening?: boolean | null | undefined
    drugError?: string
  } | null>({})
  const [hasChanged, setHasChanged] = useState('')

  const backAction = () => {
    window.removeEventListener('popstate', backAction)
    if (hasUpdated.current?.changed && !window.forceBack) {
      window.update = {
        changed: hasUpdated.current.changed,
        isPhoneVisitAllowed: hasUpdated.current.isPhoneVisitAllowed,
        drugScreeningText: hasUpdated.current.drugScreeningText,
        requiresDrugScreening: hasUpdated.current.requiresDrugScreening,
        drugError: hasUpdated.current.drugError ?? '',
      }
      window.history.forward()
    }
  }

  useEffect(() => {
    window.addEventListener('popstate', backAction)

    if (window.update) {
      setRequiresDrugScreening(window.update.requiresDrugScreening ?? false)
      setIsPhoneVisitAllowed(window.update.isPhoneVisitAllowed ?? false)

      setTimeout(() => {
        setDrugScreeningText(window.update?.drugScreeningText ?? null)
        setDrugError(window.update?.drugError ?? '')
        window.update = null
      }, 5)
      setIsConfirmationPopUpOpened('back')
      hasUpdated.current = null
    }
    return () => {
      window.onEditState = true
      if (window.releaseListener) window.removeEventListener('popstate', backAction)
      window.releaseListener = null
      window.forceBack = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount to wire the popstate listener and consume any window.update handoff; deps intentionally omitted so the listener is not re-registered
  }, [])

  useEffect(() => {
    const currentSettings = {
      IsPhoneVisitCommunicationTypeAllowed: isPhoneVisitAllowed ?? false,
      DrugScreenHtml: drugScreeningText ?? null,
    }
    hasUpdated.current = {
      changed: hasSettingsChanged(currentSettings, stateSettings),
      isPhoneVisitAllowed,
      drugScreeningText,
      requiresDrugScreening,
      drugError,
    }
    setHasChanged(hasUpdated.current.changed ? 'changed' : '')

    window.onbeforeunload = () => {
      if (hasUpdated.current?.changed) {
        return ACCOUNT_SETTINGS_RELOAD_PAGE_POP_UP_TEXT
      }
      return undefined
    }
    return () => {
      window.onbeforeunload = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stateSettings is frozen at mount via useState and never changes, so it is intentionally excluded to avoid re-running the change-detection effect
  }, [isPhoneVisitAllowed, drugScreeningText, requiresDrugScreening, drugError])

  useEffect(() => {
    if (requiresDrugScreening) {
      // Restore the stored HTML (not stripped) so the editor shows formatting when re-enabled.
      setDrugScreeningText(stateSettings.DrugScreenHtml ?? '')
    } else {
      setDrugScreeningText(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setDrugScreeningText and stateSettings.DrugScreenHtml are stable across the component lifetime; only requiresDrugScreening should drive this restore effect
  }, [requiresDrugScreening])

  useEffect(() => {
    if (error) {
      setTimeout(() => {
        setError('')
      }, 3500)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setError is a stable React state setter and is intentionally excluded; only error should drive this auto-clear effect
  }, [error])

  return { hasChanged }
}
